import React, { useState, useEffect } from "react";
import { FaPlus, FaShieldAlt } from "react-icons/fa";
import { getFamilyMembers, removeFamilyMember } from "../api/family.api";
import { getStaffProfile } from "../api/staff.api";
import { getStaffId } from "../utils/storage";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import FamilyMemberModal from "../Components/FamilyMemberModal";
import ConfirmModal from "../Popups/ConfirmModal";
import LoadingState from "../Components/States/LoadingState";

const PLAN_SLOTS = 5;

const healthSummary = (m) => {
  const parts = [];
  if (m.BMI) parts.push(`BMI: ${m.BMI}`);
  if (m.BP) parts.push(`BP: ${m.BP}`);
  return parts.length ? parts.join(" | ") : "—";
};

const FamilyPage = () => {
  const staffId = getStaffId();
  const [staffProfile, setStaffProfile] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [selectedMember, setSelectedMember] = useState(null);
  const [isPrimaryModal, setIsPrimaryModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const load = () => {
    if (!staffId) {
      setError("No staffId found");
      setLoading(false);
      return;
    }
    Promise.all([
      getStaffProfile(staffId).catch(() => ({ data: {} })),
      getFamilyMembers(staffId).catch(() => ({ data: { family_members: [] } })),
    ]).then(([profileRes, famRes]) => {
      if (profileRes.data?.staff) setStaffProfile(profileRes.data.staff);
      setFamilyMembers(famRes.data?.family_members || []);
      setLoading(false);
    });
  };

  useEffect(() => {
    load();
  }, [staffId]);

  const slotsUsed = 1 + familyMembers.length;
  const slotsPct = Math.min(100, (slotsUsed / PLAN_SLOTS) * 100);

  const openView = (member, primary = false) => {
    setSelectedMember(primary ? { ...staffProfile, isPrimary: true, relation: "Primary", fullName: staffProfile?.name } : member);
    setIsPrimaryModal(primary);
    setModalMode("view");
    setModalOpen(true);
  };

  const openCreate = () => {
    setSelectedMember(null);
    setIsPrimaryModal(false);
    setModalMode("create");
    setModalOpen(true);
  };

  const openEdit = (member) => {
    setSelectedMember(member);
    setIsPrimaryModal(false);
    setModalMode("edit");
    setModalOpen(true);
  };

  const handleSaved = (member, action) => {
    if (action === "create") setFamilyMembers((prev) => [...prev, member]);
    else setFamilyMembers((prev) => prev.map((m) => (m._id === member._id ? member : m)));
  };

  const confirmDelete = () => {
    if (!pendingDeleteId) return;
    removeFamilyMember(staffId, pendingDeleteId)
      .then(() => setFamilyMembers((prev) => prev.filter((m) => m._id !== pendingDeleteId)))
      .catch(() => setError("Error removing family member"))
      .finally(() => setPendingDeleteId(null));
  };

  const primaryImage =
    staffProfile?.profileImage ||
    staffProfile?.image ||
    "https://www.jrplumbingandheatingltd.co.uk/wp-content/uploads/2019/03/Anon.jpg";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <div className="flex-1 pb-16 lg:pb-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pt-20">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-on-primary-fixed-var">Family Management</h1>
              <p className="text-on-surface-variant mt-1">
                View and manage dependents covered under your Elthium Health plan.
              </p>
            </div>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white font-semibold rounded-lg shrink-0"
            >
              <FaPlus size={14} /> Add Family Member
            </button>
          </div>

          {error && <p className="text-red-500 mb-4">{error}</p>}
          {loading ? (
            <LoadingState branded message="Loading family members…" className="py-12" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {staffProfile && (
                <article className="bg-surface-lowest border border-surface-container rounded-xl p-5 flex flex-col">
                  <div className="flex items-start gap-3 mb-4">
                    <img src={primaryImage} alt="" className="w-14 h-14 rounded-full object-cover" />
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="font-bold text-on-primary-fixed-var capitalize">{staffProfile.name}</h2>
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-primary text-white px-2 py-0.5 rounded flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-300" /> Primary
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3 flex-1 text-sm">
                    <div>
                      <p className="text-[10px] font-bold text-on-surface-variant uppercase">Age</p>
                      <p className="font-medium">{staffProfile.age ? `${staffProfile.age} Years` : "—"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-on-surface-variant uppercase">Health Summary</p>
                      <p className="font-medium text-on-surface-variant">{healthSummary(staffProfile)}</p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-4 mt-4 pt-4 border-t border-surface-container text-sm font-medium">
                    <button type="button" onClick={() => openView(staffProfile, true)} className="text-primary hover:underline">
                      View Details
                    </button>
                  </div>
                </article>
              )}

              {familyMembers.map((member) => (
                <article key={member._id} className="bg-surface-lowest border border-surface-container rounded-xl p-5 flex flex-col">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-14 h-14 rounded-full bg-surface-container flex items-center justify-center text-lg font-bold text-on-surface-variant">
                      {member.fullName?.charAt(0) || "?"}
                    </div>
                    <div>
                      <h2 className="font-bold text-on-primary-fixed-var">{member.fullName}</h2>
                      <span className="text-xs text-on-surface-variant bg-surface-low px-2 py-0.5 rounded-full">
                        {member.relation || "Dependent"}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3 flex-1 text-sm">
                    <div>
                      <p className="text-[10px] font-bold text-on-surface-variant uppercase">Age</p>
                      <p className="font-medium">{member.age ? `${member.age} Years` : "—"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-on-surface-variant uppercase">Health Summary</p>
                      <p className="font-medium text-on-surface-variant">{healthSummary(member)}</p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-4 mt-4 pt-4 border-t border-surface-container text-sm font-medium">
                    <button type="button" onClick={() => openEdit(member)} className="text-primary hover:underline">Edit</button>
                    <button type="button" onClick={() => openView(member)} className="text-primary hover:underline">View Details</button>
                    <button type="button" onClick={() => setPendingDeleteId(member._id)} className="text-red-500 hover:underline text-xs">Delete</button>
                  </div>
                </article>
              ))}

              <article className="bg-surface-lowest border border-surface-container rounded-xl p-6 flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-primary-fixed/40 flex items-center justify-center mb-3">
                  <FaShieldAlt className="text-primary text-xl" />
                </div>
                <h2 className="font-bold text-on-primary-fixed-var mb-2">Plan Capacity</h2>
                <p className="text-sm text-on-surface-variant mb-4">
                  You have {Math.max(0, PLAN_SLOTS - slotsUsed)} remaining dependent slot{PLAN_SLOTS - slotsUsed !== 1 ? "s" : ""} available on your current Family Wellness Tier.
                </p>
                <div className="w-full h-2 bg-surface-container rounded-full overflow-hidden mb-2">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${slotsPct}%` }} />
                </div>
                <p className="text-xs text-on-surface-variant">{slotsUsed} of {PLAN_SLOTS} slots used</p>
              </article>
            </div>
          )}
        </div>
        <Footer />
      </div>

      <FamilyMemberModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        member={selectedMember}
        isPrimary={isPrimaryModal}
        staffId={staffId}
        onSaved={handleSaved}
      />

      <ConfirmModal
        open={!!pendingDeleteId}
        title="Remove family member?"
        message="This action cannot be undone."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setPendingDeleteId(null)}
      />
    </div>
  );
};

export default FamilyPage;
